<template>
	<div class="text-group">
		<template v-for="(field, index) in fields">
			<template v-if="field.type === 'checkbox'">
				<checkbox
					:key="'settings-checkbox-' + index"
					:id="'settings-checkbox-' + index"
					small
					labelSlot
					v-model="internalValues[field.name]"
					@input="onFieldChange(field)"
				>
					{{ field.label }}
					<help v-if="field.helpText" class="ml-4" :text="field.helpText" />
				</checkbox>
				<label
					v-if="field.description"
					class="checkbox-description"
					:key="'settings-checkbox-label' + index"
					:for="'settings-checkbox-' + index"
				>
					{{ field.description }}
				</label>
			</template>
			<template v-else-if="field.type === 'textarea'">
				<textarea-text
					:key="'xform_'+_uid+'textarea_'+index"
					:name="field.name"
					@input="onFieldInput(field)"
					@change="onFieldChange(field)"
					v-model="internalValues[field.name]"
					:id="'xform-' + field.name"
					:placeholder="field.placeholder || field.label"
				/>
			</template>
			<template v-else-if="field.type === 'color'">
				<div
					class="input-text input-color has-placeholder active"
					:key="'xform_'+_uid+'input_'+index"
				>
					<input
						v-model="internalValues[field.name]"
						class="color-input"
						:type="field.type"
						:name="field.name"
						:id="'xform-' + field.name"
						@input="onFieldInput(field)"
						@change="onFieldChange(field)"
					/>
					<div class="input-placeholder" style="height: 10px;">
						<span class="tiny">{{ field.label }}</span>
					</div>
				</div>
			</template>
			<template v-else>
				<help v-if="field.helpText" :text="field.helpText" :key="'help'+_uid+'input_'+index" />
				<input-text
					:key="'xform_'+_uid+'input_'+index"
					:type="field.type"
					:name="field.name"
					@input="onFieldInput(field)"
					@change="onFieldChange(field)"
					v-model="internalValues[field.name]"
					:id="'xform-' + field.name"
					:placeholder="field.placeholder || field.label"
					:readonly="field.disabled"
					:pattern="field.pattern"
					:inputmode="field.inputmode"
					:required="field.required"
					:emailComparison="field.emailComparison"
					:passwordComparison="field.passwordComparison"
					:submitError="errorStatus[field.name]"
					checkoutValidation
					@inputValid="setValidStatus"
					no-validation
					hide-valid
				/>
			</template>
		</template>
	</div>
</template>

<script>
export default {
	props: {
		fields: {
			type: Array,
			required: true,
		},
		value: {
			type: Object,
			required: true,
		},
		submitErrors: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			internalValues: {},
			validatorStatus: {},
			validStatus: {},
			errorStatus: {},
			invalidArray: [],
		};
	},
	mounted () {
		const fields = {};
		const validatorStatus = {};
		this.fields.forEach(field => {
			if (process.env.NODE_ENV !== 'production' && fields[field.name] !== undefined) {
				throw new Error('Du har angett fältnamnet "' + field.name + '" flera gånger!');
			}
			fields[field.name] = '';
			validatorStatus[field.name] = null;
		});
		this.validatorStatus = validatorStatus;
		this.internalValues = Object.assign(fields, this.internalValues, this.value);
	},
	methods: {
		validate (field) {
			if (field.validator) {
				this.validatorStatus[field.name] = field.validator(this.internalValues[field.name], this.internalValues);
			} else {
				this.validatorStatus[field.name] = true;
			}
		},
		onFieldChange (field) {
			const value = this.internalValues[field.name];
			this.validate(field);
			if (this.value[field.name] !== value) {
				this.$emit('input', this.internalValues);
				this.$emit('change', this.internalValues);
			}
		},
		onFieldInput (field) {
			const value = this.internalValues[field.name];
			this.validate(field);
			if (this.value[field.name] !== value) {
				this.$emit('input', this.internalValues);
			}
		},
		setValidStatus (to) {
			// Spread properties of observable to a regular object
			let newStatus = { ...this.validStatus };
			let name = to.name;
			let value = to.valid;
			newStatus[name] = value;

			// Singles out the password fields so they're not checked unless all filled out
			if (name === 'oldPassword' || name === 'newPassword' || name === 'newPasswordRepeat') {
				let passwordArray = ['oldPassword', 'newPassword', 'newPasswordRepeat'];

				// If newPassword and newPasswordRepeat are empty, all passwordfields are valid
				if (! this.internalValues.newPassword && ! this.internalValues.newPasswordRepeat) {
					passwordArray.forEach(key => {
						newStatus[key] = true;
					});
				} else {
					newStatus[name] = value;
					passwordArray.forEach(key => {
						if (! this.internalValues[key]) {
							newStatus[key] = false;
						}
					});
				}
				this.validStatus = newStatus;
			} else if (this.validStatus[name] === 'undefined' || this.validStatus[name] !== newStatus[name] ) {
				this.validStatus = newStatus;
			}
		},
		setInvalidArray (statuses) {
			let invalidArray = [];

			Object.keys(statuses).forEach(function(key) {
				if (! statuses[key]) {
					invalidArray.push(key);
				}
			});

			return invalidArray;
		},
		// If user tries to save with invalid input fields, change error statuses to make input fields red
		setSubmitErrors () {
			let errorObj = {};

			this.invalidArray.forEach(item => {
				errorObj[item] = {status: true};
				this.validStatus[item] = false;
			});

			this.errorStatus = errorObj;
		},
	},
	validators: {
		combine (...validators) {
			return function (value, allValues) {
				for (const i in validators) {
					if (! validators[i](value, allValues)) {
						return false;
					}
				}
				return true;
			};
		},
		length (minLength, maxLength) {
			return function (value, allValues) {
				return (
					(minLength === undefined || value.length >= minLength) &&
					(maxLength === undefined || value.length <= maxLength)
				);
			};
		},
		aToZ () {
			return function (value, allValues) {
				return /^[a-zA-Z]+$/.test(value);
			};
		},
		aToÖ () {
			return function (value, allValues) {
				return /^[a-öA-Ö]+$/.test(value);
			};
		},
		address () {
			return function (value, allValues) {
				if (value.length < 4) {
					return false;
				}
				return /^[a-øA-Ø\d\s,\-]+$/.test(value);
			};
		},
		numeric (allowWhitespace) {
			return function (value, allValues) {
				if (allowWhitespace) {
					return /^[\d\s]*$/.test(value);
				}
				return /^\d+$/.test(value);
			};
		},
		numericPlus () {
			return function (value, allValues) {
				return /^[\d\s-]+$/.test(value);
			};
		},
		mustMatch (otherFieldName) {
			return function (value, allValues) {
				// @TODO kanske ska lägga in denna i någon lista, så att den kan valideras om när fältet som det ska matchas med uppdateras
				return value === allValues[otherFieldName];
			};
		},
		email () {
			return function (value) {
				return value.length > 5 && value.length < 24 && value.indexOf('@') >= 0 && value.indexOf('.') >= 0;
			};
		},
		null () {
			return function () {
				return null;
			};
		},
		noPositive (validator) {
			return function (value, allValues) {
				return validator(value, allValues) && null;
			};
		},
	},
	watch: {
		value: {
			handler (to, frm) {
				const oldValues = JSON.parse(JSON.stringify(this.internalValues));
				this.internalValues = Object.assign({}, this.internalValues, to);
				for (const k in this.fields) {
					const f = this.fields[k];
					if (to[f.name] === oldValues[f.name]) {
						continue;
					}
					this.onFieldChange(f);
				}
			},
			deep: true,
		},
		validStatus (to) {
			this.invalidArray = this.setInvalidArray(to);
			let validStatusArray = [];

			for(let i = 0; i < this.fields.length; i++) {
				let item = this.fields[i].name;

				validStatusArray[item] = this.validStatus[item];
			}

			this.$emit('updateValidStatusArray', validStatusArray);
		},
		submitErrors () {
			this.setSubmitErrors();
		},
	},
};
</script>
